import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const Ctx = createContext({ flags: {}, theme: null, announcement: null });
export const useConfig = () => useContext(Ctx);
export const flagOn = (flags, key) => flags?.[key] !== false; // default on

// #RRGGBB -> { h, s, l } (HSL components) + relative luminance (0..1)
function parseAccent(hex) {
  const h = (hex || "").replace("#", "");
  if (!/^[0-9a-f]{6}$/i.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh, s, l = (max + min) / 2;
  if (max === min) { hh = 0; s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      default: hh = (r - g) / d + 4;
    }
    hh /= 6;
  }
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  return { h: Math.round(hh * 360), s: Math.round(s * 100), l: Math.round(l * 100), lum };
}

function applyAccent(accent) {
  const c = parseAccent(accent);
  const root = document.documentElement;
  if (!c) return;
  // Full, contrast-safe accent palette (HSL components, matching tailwind's hsl(var(--x)))
  root.style.setProperty("--primary", `${c.h} ${c.s}% ${c.l}%`);
  root.style.setProperty("--ring", `${c.h} ${c.s}% ${c.l}%`);
  root.style.setProperty("--primary-foreground", c.lum > 0.45 ? "222 47% 11%" : "0 0% 100%");
  root.style.setProperty("--chart-1", `${c.h} ${c.s}% ${c.l}%`);
}

export function ConfigProvider({ children }) {
  const [cfg, setCfg] = useState({ flags: {}, theme: null, announcement: null });

  useEffect(() => {
    api.get("/config").then(r => {
      setCfg({ flags: r.data.feature_flags || {}, theme: r.data.theme, announcement: r.data.announcement });
      if (r.data.theme?.accent) applyAccent(r.data.theme.accent);
    }).catch(() => {});
  }, []);

  return <Ctx.Provider value={cfg}>{children}</Ctx.Provider>;
}
